import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Descriptions,
  Form,
  Input,
  Modal,
  Select,
  Table,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  createSuperAdminClinicUser,
  getSuperAdminClinic,
  getSuperAdminClinicUsers,
} from "../../apis/superadmin";
import { format } from "date-fns";

const AVAILABLE_ROLES = ["admin", "doctor", "frontdesk", "nursing"];

const ClinicDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [clinic, setClinic] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [loadingClinic, setLoadingClinic] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const clinicId = Number(id);

  useEffect(() => {
    getSuperAdminClinic(clinicId)
      .then((res: any) => setClinic(res.data))
      .finally(() => setLoadingClinic(false));

    fetchUsers();
  }, [clinicId]);

  const fetchUsers = () => {
    setLoadingUsers(true);
    getSuperAdminClinicUsers(clinicId)
      .then((res: any) => setUsers(res.data))
      .finally(() => setLoadingUsers(false));
  };

  const handleAddUser = (values: any) => {
    setSubmitting(true);
    createSuperAdminClinicUser(clinicId, values)
      .then(() => {
        setShowAddUser(false);
        form.resetFields();
        fetchUsers();
      })
      .finally(() => setSubmitting(false));
  };

  const userColumns: ColumnsType<any> = [
    {
      title: "Name",
      render: (_, row) => `${row.firstName} ${row.lastName}`,
    },
    { title: "Email", dataIndex: "email" },
    { title: "Phone", dataIndex: "phone", render: (v) => v ?? "—" },
    {
      title: "Roles",
      dataIndex: "roles",
      render: (roles: string[]) =>
        roles?.map((r) => <Tag key={r}>{r}</Tag>),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (v) => (
        <Tag color={v ? "success" : "default"}>{v ? "Active" : "Inactive"}</Tag>
      ),
    },
  ];

  if (loadingClinic) return <div className="p-8 text-gray-500">Loading...</div>;
  if (!clinic) return <div className="p-8 text-red-500">Clinic not found.</div>;

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate("/superadmin")}
          className="text-sm text-gray-500 hover:text-gray-800"
        >
          ← Clinics
        </button>
        <span className="text-gray-300">/</span>
        <h1 className="text-xl font-bold text-gray-900">{clinic.name}</h1>
        <Tag color={clinic.isActive ? "success" : "error"}>
          {clinic.isActive ? "Active" : "Inactive"}
        </Tag>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Email">{clinic.email}</Descriptions.Item>
          <Descriptions.Item label="Phone">{clinic.phone}</Descriptions.Item>
          <Descriptions.Item label="Plan">
            <Tag>{clinic.plan?.toUpperCase()}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Address">{clinic.address}</Descriptions.Item>
          <Descriptions.Item label="Total Users">{clinic.usersCount ?? clinic.users?.length}</Descriptions.Item>
          <Descriptions.Item label="Total Patients">{clinic.patientsCount}</Descriptions.Item>
          <Descriptions.Item label="Registered">
            {clinic.createdAt ? format(new Date(clinic.createdAt), "dd MMM yyyy") : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Admin">
            {clinic.adminFirstName} {clinic.adminLastName}
          </Descriptions.Item>
        </Descriptions>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Users</h2>
        <Button type="primary" onClick={() => setShowAddUser(true)}>
          + Add User
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <Table
          rowKey="id"
          dataSource={users}
          columns={userColumns}
          loading={loadingUsers}
          pagination={false}
        />
      </div>

      <Modal
        title="Add User to Clinic"
        open={showAddUser}
        onCancel={() => { setShowAddUser(false); form.resetFields(); }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleAddUser} className="mt-2">
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </div>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: "email" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="Phone (10 digits)">
            <Input maxLength={10} />
          </Form.Item>
          <Form.Item name="roles" label="Roles" rules={[{ required: true }]}>
            <Select mode="multiple" placeholder="Select roles">
              {AVAILABLE_ROLES.map((r) => (
                <Select.Option key={r} value={r}>{r}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <div className="flex justify-end gap-2 mt-2">
            <Button onClick={() => { setShowAddUser(false); form.resetFields(); }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Create User
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ClinicDetail;
